/**
 * What a zone-scoped caller may do, read off the surfaces answer (`KLQ-20261028-008`).
 *
 * Pure, and beside `tabs.ts` for the same reason: these are rules, and a rule inside a `.tsx`
 * is a rule nobody can exercise (ADR-0015). **It is navigation, not authorization.** The server
 * refuses every one of these writes on its own; this only stops the client drawing a door that
 * answers `403` or `404` — the owner's decision 4 of 2026-10-29.
 *
 * The two server rules it mirrors, and where they live:
 *
 * - **Project settings are unscoped-only.** `projects.service.UNSCOPED_ONLY_ACTIONS` refuses
 *   every `edit_project_settings`, `update_project` and `archive_project` write (the project's
 *   own fields, archiving, phases, naming template, disciplines, document types, parties,
 *   roles, objects, labels) to a caller with zones. Admin, manager and the *lead* architect —
 *   an architect with no zones — keep them.
 * - **A child list is part of its parent.** `hierarchy.scope.require_writable`: copying,
 *   grouping, moving or removing a node writes its parent, so a scoped caller may do it only
 *   when the parent lies inside a zone. That is what refuses those acts on a granted node
 *   itself (its parent is outside) while allowing them on everything beneath it. Renaming
 *   writes the node's own fields, so it needs the node itself inside a zone.
 *
 * `scope_node_ids: null` (or absent) is unrestricted, exactly as on the server — an empty
 * list is the other extreme and grants nothing.
 */

import type { ProjectSurfaces } from './api';
import { accessTo } from './tabs';

/** The caller's zone grants, or `null` when they reach the whole project. */
export function grantedNodes(surfaces: ProjectSurfaces | undefined): readonly string[] | null {
  return surfaces?.scope_node_ids ?? null;
}

/** True when the caller's access to this project is limited to some zones. */
export function isScoped(surfaces: ProjectSurfaces | undefined): boolean {
  return grantedNodes(surfaces) !== null;
}

/**
 * Whether the project-settings controls are drawn as editable — the settings surface at
 * `write`, and no zones. Covers the project's own fields and archiving too (owner, 2026-10-29).
 */
export function mayEditProjectSettings(surfaces: ProjectSurfaces | undefined): boolean {
  return accessTo(surfaces, 'settings') === 'write' && !isScoped(surfaces);
}

/** A node's parent id, or `null`/`undefined` at the root or for an id the caller does not hold. */
export type ParentOf = (nodeId: string) => string | null | undefined;

/**
 * Whether `nodeId` is a granted node or lies beneath one. Walks up through `parentOf`; a node
 * whose chain breaks before reaching a grant is outside, which is the safe direction.
 */
export function withinZones(
  nodeId: string | null | undefined,
  parentOf: ParentOf,
  granted: readonly string[] | null,
): boolean {
  if (granted === null) return true;
  const grants = new Set(granted);
  const seen = new Set<string>();
  let current = nodeId ?? null;
  while (current !== null && !seen.has(current)) {
    if (grants.has(current)) return true;
    seen.add(current);
    current = parentOf(current) ?? null;
  }
  return false;
}

/** What may be drawn on one tree node for this caller, on top of their surface access. */
export interface NodeActions {
  /** Rename and the node's other own fields — the node itself inside a zone. */
  readonly edit: boolean;
  /** Copy, group, move, remove — the node's **parent** inside a zone. */
  readonly restructure: boolean;
}

export function nodeActions(
  nodeId: string,
  parentOf: ParentOf,
  granted: readonly string[] | null,
): NodeActions {
  return {
    edit: withinZones(nodeId, parentOf, granted),
    restructure: withinZones(parentOf(nodeId), parentOf, granted),
  };
}
